import { debugModeAtom } from "@/atoms/debugModeAtom";
import { SwipeBar } from "@/common/SwipeBar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAtomValue } from "jotai";
import { BadgeIcon, Check, CheckIcon, Copy, EyeOff } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.info("スポット名をコピーしました");
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={handleCopy}>
      {copied ? (
        <Check size={14} className="text-green-600" />
      ) : (
        <Copy size={14} className="text-gray-400 hover:text-gray-600" />
      )}
    </Button>
  );
};

export interface SpotCardProps {
  spotId: string;
  name?: string;
  distance?: number;
  googleCid?: string;
  isLoading?: boolean;
  error?: Error | null;
  isLoaded?: boolean;
  meta?: React.ReactNode;
  hideSwipeBars?: boolean;
  leftSwipeAction?: "bookmark" | "flag" | "delete";
  rightSwipeAction?: "bookmark" | "flag" | "delete";
  backgroundOpacity?: number;
  showCount?: boolean;
  savedTotalCount?: number;
  visitedTotalCount?: number;
  showBadge?: boolean;
  onLeftTap?: () => void;
  onRightTap?: () => void;
  isProcessing?: boolean;
  onDislikeClick?: () => void;
  isDisliked?: boolean;
}

export const SpotCard = ({
  spotId,
  name,
  distance,
  googleCid,
  isLoading = false,
  error = null,
  isLoaded = true,
  meta,
  hideSwipeBars = false,
  leftSwipeAction,
  rightSwipeAction,
  backgroundOpacity = 0.6,
  showCount = false,
  savedTotalCount,
  visitedTotalCount,
  showBadge,
  onLeftTap,
  onRightTap,
  isProcessing = false,
  onDislikeClick = () => {},
  isDisliked = false
}: SpotCardProps) => {
  const debugMode = useAtomValue(debugModeAtom);
  return (
    <div className="relative h-32">
      {/* 背景コンテナ（グラデーション + 白背景） */}
      <div
        className="absolute inset-0 transition-opacity duration-300"
        style={{ opacity: backgroundOpacity }}
      >
        {/* グラデーション背景 */}
        <div
          className={`absolute inset-0 rounded-[32px] ${
            isLoaded ? "bg-gradient-to-br from-[#0fff78] to-[#86faff]" : "bg-muted"
          }`}
        />
        {/* 白い背景 */}
        <div className="absolute inset-1.5 rounded-[26px] bg-white" />
      </div>

      {/* プレゼンテーションレイヤー */}
      <div className="relative flex h-full items-center justify-center p-1.5">
        <div className="flex h-full w-full justify-center">
          {!hideSwipeBars && (
            <SwipeBar
              side="left"
              muted={!isLoaded}
              actionType={leftSwipeAction}
              count={showCount ? savedTotalCount ?? 0 : undefined}
              onClick={onLeftTap}
              isProcessing={isProcessing}
            />
          )}
          <div className="flex h-full w-full items-center justify-center p-4 pb-2">
            {isLoading ? (
              <div className="grid gap-4">
                <Skeleton className="h-6 w-[200px]" />
                <div className="flex items-center justify-center gap-2">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <Skeleton className="h-12 w-12 rounded-full" />
                </div>
              </div>
            ) : error ? (
              <span className="">😔読み込みエラー</span>
            ) : name ? (
              <div className="grid gap-2">
                <div className="text-center">
                  <div className=" flex  items-center justify-center gap-1">
                    <div className="relative flex w-6 items-center justify-center">
                      {showBadge && (
                        <>
                          <BadgeIcon size={16} className="text-[#4faee5]" fill="#4faee5" />
                          <CheckIcon
                            strokeWidth={5}
                            size={9}
                            className="absolute mt-[1px] text-white"
                          />
                        </>
                      )}
                    </div>
                    <h2 className="text-md mb-0.5 min-w-0 max-w-[200px] truncate font-semibold">
                      {name}
                    </h2>
                    <div className="flex w-6 items-center justify-center">
                      <CopyButton text={name || ""} />
                    </div>
                  </div>
                  {meta && <div className="mt-1 text-xs text-gray-500">{meta}</div>}
                  {distance && (
                    <div className="mt-1 text-xs text-gray-500">{distance.toFixed(2)}km</div>
                  )}
                </div>
                <div className="flex items-center justify-center gap-2">
                  {[
                    {
                      name: "Google",
                      imageSrc: "/sns-google.svg",
                      externalLink: googleCid
                        ? `https://www.google.com/maps/?cid=${googleCid}`
                        : "https://www.google.com/maps"
                    },
                    {
                      name: "Instagram",
                      imageSrc: "/sns-instagram.svg",
                      externalLink: `https://instagram.com/explore/tags/${encodeURIComponent(
                        (name ?? "").replace(/\s+/g, "")
                      )}`
                    },
                    {
                      name: "TikTok",
                      imageSrc: "/sns-tiktok.svg",
                      externalLink: "https://www.tiktok.com/tag/" + encodeURIComponent(name ?? "")
                    },
                    {
                      name: "X",
                      imageSrc: "/sns-x.svg",
                      externalLink: `https://x.com/search?q=${encodeURIComponent(name ?? "")}`
                    }
                  ].map(({ name, imageSrc, externalLink }, index) => (
                    <Button
                      key={index}
                      variant="ghost"
                      size="icon"
                      className="h-12 w-12 rounded-full"
                      asChild
                    >
                      <a href={externalLink} target="_blank" rel="noopener noreferrer">
                        <img src={imageSrc} alt={name} className="h-8 w-8" />
                      </a>
                    </Button>
                  ))}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-12 w-12 rounded-full opacity-60"
                    onClick={onDislikeClick}
                    disabled={isDisliked}
                  >
                    <EyeOff className="h-6 w-6" />
                  </Button>
                </div>
                {debugMode && (
                  <div className="text-center text-xs text-gray-500">
                    <p>
                      ID:{" "}
                      <a
                        href={`https://dashboard.core.refloop.nicprdcts.com/spots/${spotId}`}
                        className="underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {spotId}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <span className="text-gray-500">データが見つかりませんでした</span>
            )}
          </div>
          {!hideSwipeBars && (
            <SwipeBar
              side="right"
              muted={!isLoaded}
              actionType={rightSwipeAction}
              count={showCount ? visitedTotalCount ?? 0 : undefined}
              onClick={onRightTap}
              isProcessing={isProcessing}
            />
          )}
        </div>
      </div>
    </div>
  );
};
